import React from 'react';
import { useTranslation } from 'react-i18next';
import { GridTotalsByCurrency } from '@components-library/Grid/GridTotals/GridTotalsByCurrency';
import { TableProps } from '@components-shared/table/types';
import { keyMap } from '@locales/lokalise/keyMap';
import { getCurrentDayUtc } from '@utils/library/dateTime';
import Table from 'components/shared/table/index';
import { getFormattedNumber } from 'utils/helper';
import {
  DisplayValue,
  ITradingVolumeRow,
  ITradingVolume,
  IValue,
  AccountKeyVolume,
  SymbolKeyVolume,
  ICommission,
  IVolumeBySymbol,
  ApiVolumeReportAdminGridTotalsItem,
} from '../types';

interface ITradingVolumeReportProps {
  api: string;
  exportAPI: string;
  totalApi: string;
  isUserArea?: boolean;
}

const renderVolumeBy = (title: string, value: IValue[], key: DisplayValue) => (
  <details>
    <summary>{`${title}:`}</summary>
    <div>
      {value.map((v) => {
        const displayValue = `${v[key as DisplayValue]}: ${getFormattedNumber(v.volume, { precision: true })}`;

        if (v.bySymbol && v.bySymbol.length) {
          return (
            <details key={v.account}>
              <summary>{displayValue}</summary>
              <div>
                {v.bySymbol.map((item: IVolumeBySymbol) => (
                  <div className="table__nowrap" key={item.symbol}>{`${item.symbol}: ${item.volume}`}</div>
                ))}
              </div>
            </details>
          );
        }

        return (
          <div className="table__nowrap" key={v[key as DisplayValue]}>
            {displayValue}
          </div>
        );
      })}
    </div>
  </details>
);

const ColumnValue = ({
  row,
  value,
  volumeBySymbolKey,
  volumeByAccountKey,
}: {
  row: ITradingVolumeRow;
  value: ITradingVolume;
  volumeByAccountKey: AccountKeyVolume;
  volumeBySymbolKey: SymbolKeyVolume;
}): JSX.Element | null => {
  const { i18n } = useTranslation();

  if (!value) {
    return null;
  }
  const totalNonEcn = getFormattedNumber(value.totalNonEcn, { precision: true });
  const totalEcn = getFormattedNumber(value.totalEcn, { precision: true });

  return (
    <div>
      <div className="table__nowrap">{`${i18n.t(
        keyMap.partnershipIbInfoStatisticsTradingVolReportTotalNonEcn,
      )}: ${totalNonEcn}`}</div>
      <div className="table__nowrap">{`${i18n.t(
        keyMap.partnershipIbInfoStatisticsTradingVolReportTotalEcn,
      )}: ${totalEcn}`}</div>
      {renderVolumeBy(
        i18n.t(keyMap.partnershipIbInfoStatisticsTradingVolReportByAccount),
        row[volumeByAccountKey] || [],
        'account',
      )}
      {renderVolumeBy(
        i18n.t(keyMap.partnershipIbInfoStatisticsTradingVolReportBySymbol),
        row[volumeBySymbolKey] || [],
        'symbol',
      )}
    </div>
  );
};

const renderClients = (row: ITradingVolumeRow, value: number): string => `${value}/${row.subIbsAmount}`;

const renderCommissions = (row: ITradingVolumeRow, value: ICommission) => (
  <details>
    <summary>{getFormattedNumber(value.commission, { precision: true })}</summary>
    <div>
      {value.commissionSenders.map((item) => (
        <div className="table__nowrap" key={item.sender}>
          {`${item.sender || ''}: ${getFormattedNumber(item.commission, { precision: true })}`}
        </div>
      ))}
    </div>
  </details>
);

export const COLUMNS = [
  {
    key: 'mainIbCode',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportMainIbCode,
  },
  {
    key: 'partnerAccount',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportPartnerAccount,
    render: (row: ITradingVolumeRow, value: string): JSX.Element => (
      <details>
        <summary>{value}</summary>
        <div>{row.email}</div>
      </details>
    ),
  },
  {
    key: 'level',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportLevel,
  },
  {
    key: 'totalClients',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportTotalClientsSubIb,
    render: renderClients,
  },
  {
    key: 'bdmCode',
    name: keyMap.sharedFieldsBdmCode,
  },
  {
    key: 'aeCode',
    name: keyMap.sharedFieldsAeCode,
  },
  {
    key: 'ownTradingVolume',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportOwnTradingVolume,
    render: (row: ITradingVolumeRow, val: ITradingVolume): JSX.Element | null => (
      <ColumnValue
        row={row}
        value={val}
        volumeByAccountKey="ownTradingVolumeByAccount"
        volumeBySymbolKey="ownTradingVolumeBySymbol"
      />
    ),
  },
  {
    key: 'partnerTradingVolume',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportPartnerTradingVolume,
    render: (row: ITradingVolumeRow, val: ITradingVolume): JSX.Element | null => (
      <ColumnValue
        row={row}
        value={val}
        volumeByAccountKey="partnerTradingVolumeByAccount"
        volumeBySymbolKey="partnerTradingVolumeBySymbol"
      />
    ),
  },
  {
    key: 'ownDeposit',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportOwnDepositUsd,
    render: (row: ITradingVolumeRow, value: number): string => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'partnerDeposit',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportPartnerDepositUsd,
    render: (row: ITradingVolumeRow, value: number): string => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'ownWithdrawal',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportOwnWithdrawalUsd,
    render: (row: ITradingVolumeRow, value: number): string => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'partnerWithdrawal',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportPartnerWithdrawalUsd,
    render: (row: ITradingVolumeRow, value: number): string => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'ownNetDeposit',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportOwnNetDepositUsd,
    render: (row: ITradingVolumeRow, value: number): string => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'partnerNetDeposit',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportPartnerNetDepositUsd,
    render: (row: ITradingVolumeRow, value: number): string => getFormattedNumber(value, { precision: 2 }),
  },
  {
    key: 'commission',
    name: keyMap.partnershipIbInfoStatisticsTradingVolReportCommissionUsd,
    render: (row: ITradingVolumeRow, value: ICommission): JSX.Element => renderCommissions(row, value),
  },
];

export const FILTER_FIELDS = [
  {
    name: 'period',
    title: keyMap.partnershipIbInfoStatisticsTradingVolReportPeriod,
    type: 'dateTimeRange',
    defaultValue: getCurrentDayUtc(),
  },
];

export const TradingVolumeReport = ({
  api,
  exportAPI,
  totalApi,
  isUserArea,
}: ITradingVolumeReportProps): JSX.Element => {
  const TRADING_VOLUME_REPORT_COLUMNS = isUserArea
    ? COLUMNS.map((column) => ({ ...column, isHidden: column.key === 'bdmCode' || column.key === 'aeCode' }))
    : COLUMNS;

  return (
    <Table
      tableId="trading-volume-report"
      columns={TRADING_VOLUME_REPORT_COLUMNS as TableProps['columns']}
      filterFields={FILTER_FIELDS as unknown as TableProps['filterFields']}
      api={api}
      exportAPI={exportAPI}
      apiGetTotals={{
        url: totalApi,
        totalCountExtractor: (data: ApiVolumeReportAdminGridTotalsItem) => data.totalCount,
        totalComponent: (data: ApiVolumeReportAdminGridTotalsItem) => {
          return (
            <GridTotalsByCurrency
              totalData={data}
              rowLabelsConfig={{
                ownDeposit: keyMap.partnershipIbInfoStatisticsTradingVolReportOwnDepositUsd,
                ownNetDeposit: keyMap.partnershipIbInfoStatisticsTradingVolReportOwnNetDepositUsd,
                partnerDeposit: keyMap.partnershipIbInfoStatisticsTradingVolReportPartnerDepositUsd,
                partnerNetDeposit: keyMap.partnershipIbInfoStatisticsTradingVolReportPartnerNetDepositUsd,
                ownWithdrawal: keyMap.partnershipIbInfoStatisticsTradingVolReportOwnWithdrawalUsd,
                partnerWithdrawal: keyMap.partnershipIbInfoStatisticsTradingVolReportPartnerWithdrawalUsd,
                commissionTotal: keyMap.partnershipIbInfoStatisticsTradingVolReportCommissionUsd,
              }}
            />
          );
        },
      }}
    />
  );
};
